// Hand-maintained — override generation
import { RequestBuilder } from "../request-builder";
import type {
  Permission,
  PermissionMeta,
  PermissionPreset,
} from "../_internal/types.gen";

export type { Permission, PermissionMeta, PermissionPreset };

export interface PermissionListResult {
  data: Permission[];
  meta?: { total?: number };
}

export interface PermissionResult {
  data: Permission;
}

export interface PermissionMetaResult {
  data: PermissionMeta;
}

export interface PermissionPresetListResult {
  data: PermissionPreset[];
  meta?: { total?: number };
}

export interface PermissionPresetResult {
  data: PermissionPreset;
}

export interface PermissionListParams {
  app?: string;
  context?: "workspace" | "tenant" | "own";
  category?: string;
}

export interface PermissionPresetListParams {
  context?: "tenant" | "workspace";
  app?: string;
}

export function createPermissionsNamespace(rb: RequestBuilder) {
  return {
    /**
     * List all registered platform permissions.
     *
     * Optionally filter by `app`, `context`, or `category` query params.
     *
     * @param params - Optional filter parameters (app, context, category)
     * @returns List of permissions
     * @example
     * const perms = await admin.permissions.list({ context: "workspace" });
     */
    async list(params?: PermissionListParams): Promise<PermissionListResult> {
      const query = params
        ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
        : "";
      return rb.rawGet<PermissionListResult>(`/admin/permissions${query}`);
    },

    /**
     * Get a single permission by its ID string (e.g., "extract:document:read:all").
     *
     * @param id - Permission ID string
     * @returns The permission record
     * @example
     * const perm = await admin.permissions.get("extract:document:read:all");
     */
    async get(id: string): Promise<PermissionResult> {
      return rb.rawGet<PermissionResult>(
        `/admin/permissions/${encodeURIComponent(id)}`,
      );
    },

    /**
     * Get permission system metadata (version, apps, scopes, categories, cache TTL).
     *
     * @returns Permission system metadata
     * @example
     * const meta = await admin.permissions.meta();
     */
    async meta(): Promise<PermissionMetaResult> {
      return rb.rawGet<PermissionMetaResult>(`/admin/permissions/meta`);
    },

    /**
     * List permission presets (pre-defined role bundles).
     *
     * Optionally filter by `context` (tenant, workspace) or `app`.
     *
     * @param params - Optional filter parameters (context, app)
     * @returns List of permission presets
     * @example
     * const presets = await admin.permissions.listPresets({ context: "tenant" });
     */
    async listPresets(
      params?: PermissionPresetListParams,
    ): Promise<PermissionPresetListResult> {
      const query = params
        ? `?${new URLSearchParams(params as Record<string, string>).toString()}`
        : "";
      return rb.rawGet<PermissionPresetListResult>(
        `/admin/permissions/presets${query}`,
      );
    },

    /**
     * Get a single permission preset by its composite ID (e.g., "workspace:org:admin").
     *
     * @param id - Preset composite ID
     * @returns The permission preset
     * @example
     * const preset = await admin.permissions.getPreset("workspace:org:admin");
     */
    async getPreset(id: string): Promise<PermissionPresetResult> {
      return rb.rawGet<PermissionPresetResult>(
        `/admin/permissions/presets/${encodeURIComponent(id)}`,
      );
    },
  };
}
